import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import { PaginationControl } from 'shared/components/pagination';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import {
   SimpleResume,
   SimpleResumeActionResponse,
   SimpleResumeIndexRequestDto,
   SimpleResumeIndexResponse,
} from './types';

export class SimpleResumeManagerStore {
   readonly loadResumesState = new AsyncState<SimpleResumeIndexResponse>();
   readonly activateResumeState = new AsyncState<SimpleResumeActionResponse>();
   readonly deactivateResumeState = new AsyncState<SimpleResumeActionResponse>();
   readonly deleteResumeState = new AsyncState<SimpleResumeActionResponse>();
   readonly activateAllResumesState = new AsyncState<SimpleResumeActionResponse>();
   readonly deactivateAllResumesState = new AsyncState<SimpleResumeActionResponse>();
   readonly deleteAllResumesState = new AsyncState<SimpleResumeActionResponse>();

   rawResumes: SimpleResume[] = [];
   idsArray: number[] = [];
   searchStr = '';
   totalCount = 0;
   positionFrom = 0;
   toPage = '1';

   private readonly PER_PAGE = 20;
   readonly pagination = new PaginationControl();

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawResumes: observable,
         idsArray: observable,
         searchStr: observable,
         totalCount: observable,
         positionFrom: observable,
         toPage: observable,
         resumes: computed,
         totalPages: computed,
         loading: computed,
         selectedAll: computed,
         disableBulkActions: computed,
         recordStart: computed,
         recordEnd: computed,
         onSearchChange: action.bound,
         onSelectAll: action.bound,
         onDeselectAll: action.bound,
         onToggleById: action.bound,
         onToPageChange: action.bound,
      });
   }

   get resumes(): SimpleResume[] {
      return this.rawResumes;
   }

   get totalPages(): number {
      return Math.ceil(this.totalCount / this.PER_PAGE);
   }

   get loading(): boolean {
      return this.loadResumesState.status === 'pending';
   }

   get selectedAll(): boolean {
      return this.rawResumes.length !== 0 && this.idsArray.length === this.rawResumes.length;
   }

   get disableBulkActions(): boolean {
      return this.idsArray.length === 0;
   }

   get recordStart(): number {
      return this.totalCount === 0 ? 0 : this.positionFrom + 1;
   }

   get recordEnd(): number {
      return Math.min(this.positionFrom + this.rawResumes.length, this.totalCount);
   }

   onSearchChange(value: string): void {
      this.searchStr = value;
   }

   onSelectAll(): void {
      this.idsArray = this.rawResumes.map((resume) => resume.id);
   }

   onDeselectAll(): void {
      this.idsArray = [];
   }

   onToggleById(id: number): void {
      if (this.idsArray.includes(id)) {
         this.idsArray = this.idsArray.filter((resumeId) => resumeId !== id);
      } else {
         this.idsArray = [...this.idsArray, id];
      }
   }

   onToPageChange(value: string): void {
      if (/^\d+$/.test(value) || value === '') {
         this.toPage = value;
      }
   }

   async submitSearch(): Promise<void> {
      this.pagination.setPage(0);
      this.toPage = '1';
      await this.loadResumes(0);
   }

   async loadPage(page: number): Promise<void> {
      this.pagination.setPage(page);
      this.toPage = String(page + 1);
      await this.loadResumes(page * this.PER_PAGE);
   }

   async goToPage(): Promise<void> {
      if (this.totalPages === 0) {
         return;
      }

      if (this.toPage === '') {
         this.toPage = '1';
      }

      const page = Number(this.toPage);
      if (Number.isNaN(page)) {
         return;
      }

      const nextPage = Math.min(Math.max(page, 1), this.totalPages) - 1;
      await this.loadPage(nextPage);
   }

   async loadResumes(positionFrom = this.positionFrom): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      const payload: SimpleResumeIndexRequestDto = {
         searchQuery: this.searchStr,
         positionFrom,
         limit: this.PER_PAGE,
      };

      runInAction(() => {
         this.idsArray = [];
      });

      await this.loadResumesState.run(
         () =>
            HttpClient.post<SimpleResumeIndexResponse>('/admin/simple_resumes/index', payload, {
               headers: {
                  adminid: adminID,
                  Authorization: `Bearer ${tokenKey}`,
               },
            }),
         {
            onSuccess: (resp) => {
               if (resp.status !== 200) {
                  return;
               }

               const nextPosition = Number(resp.response.positionFrom) || 0;
               const nextPage = Math.floor(nextPosition / this.PER_PAGE);
               const responseList = resp.response.simple_cv_list ?? [];

               runInAction(() => {
                  this.rawResumes = responseList;
                  this.totalCount = Number(resp.response.totalCount) || 0;
                  this.positionFrom = nextPosition;
                  this.toPage = String(nextPage + 1);
               });

               this.pagination.setPage(nextPage);
            },
         }
      );
   }

   async activateResume({
      id,
      onSuccess,
      onError,
   }: {
      id: number;
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.activateResumeState.run(
         () =>
            HttpClient.get<SimpleResumeActionResponse>(
               `/admin/simple_resumes/activate/${id}`,
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            ),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   async deactivateResume({
      id,
      onSuccess,
      onError,
   }: {
      id: number;
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivateResumeState.run(
         () =>
            HttpClient.get<SimpleResumeActionResponse>(
               `/admin/simple_resumes/deactivate/${id}`,
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            ),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   async deleteResume({
      id,
      onSuccess,
      onError,
   }: {
      id: number;
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deleteResumeState.run(
         () =>
            HttpClient.delete<SimpleResumeActionResponse>(`/admin/simple_resumes/del/${id}`, {
               headers: {
                  adminid: adminID,
                  Authorization: `Bearer ${tokenKey}`,
               },
            }),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }


   //All mass its arrays , field: "ids" [1,2,3]
   async activateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      if (this.idsArray.length === 0) {
         return;
      }

      await this.activateAllResumesState.run(
         () =>
            HttpClient.post<SimpleResumeActionResponse>(
               '/admin/simple_resumes/mass/activate',
               {
                  ids: this.idsArray,
               },
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            ),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   async deactivateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      if (this.idsArray.length === 0) {
         return;
      }

      await this.deactivateAllResumesState.run(
         () =>
            HttpClient.post<SimpleResumeActionResponse>(
               '/admin/simple_resumes/mass/deactivate',
               {
                  ids: this.idsArray,
               },
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            ),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   async deleteAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (resp: SimpleResumeActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      if (this.idsArray.length === 0) {
         return;
      }

      await this.deleteAllResumesState.run(
         () =>
            HttpClient.post<SimpleResumeActionResponse>(
               '/admin/simple_resumes/mass/delete',
               {
                  ids: this.idsArray,
               },
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            ),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadResumes(this.positionFrom);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }
}
